import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import useDataFetcher from '@hooks/useDataFetcher/useDataFetcher.ts';
import { urls } from '@api/urls.ts';
import { routes } from '@shared/constants/paths.ts';
import MainCard from '@components/ui/cards/MainCard.tsx';
import { DocumentPromptPreview } from '@app/types/document-prompt-preview';
import MainTable from '@components/MainTable';
import MainTablePagination from '@components/MainTable/MainTablePagination';
import { useTanStackTable } from '@hooks/useTanStackTable';
import { deleteDocumentPromptPreview } from '@api/document-prompt-preview';
import useToast from '@hooks/useToast';
import { documentPromptPreviewTableColumns } from './columns.tsx';

function DocumentPromptPreviewTable() {
  const navigate = useNavigate();
  const { showSuccess } = useToast();
  const { data, isLoading } = useDataFetcher<DocumentPromptPreview[]>({
    url: urls.documentPromptPreview.base,
  });

  const createDocument = () => {
    navigate(routes.superAdmin.documentPromptPreview.create);
  };

  const { table, setData } = useTanStackTable<DocumentPromptPreview>({
    tableData: [],
    columnConfig: documentPromptPreviewTableColumns,
    options: {
      meta: {
        handleDeleteRow: async (preview: DocumentPromptPreview) => {
          await deleteDocumentPromptPreview(preview.id);
          showSuccess('Success', 'Document Type has been deleted successfully');
        },
        handleEditRow: (preview: DocumentPromptPreview) => {
          navigate(routes.superAdmin.documentPromptPreview.edit(preview.id));
        },
      },
    },
  });

  useEffect(() => {
    if (!data) return;
    setData(data);
  }, [data]);

  return (
    <MainCard>
      <Stack gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h3">List of Document Types</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={createDocument}
            sx={{
              flexGrow: { xs: 1, sm: 0 },
            }}
          >
            Create Document Type
          </Button>
        </Stack>
        <Box>
          <MainTable table={table} isLoading={isLoading} />
          <MainTablePagination table={table} isLoading={isLoading} />
        </Box>
      </Stack>
    </MainCard>
  );
}

export default DocumentPromptPreviewTable;
